import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PopupDialog, TextFieldComponent, CheckboxComponent, TabPanelComponent } from '../../../../components/common';
import { UnidadMedidaSelect, EmpaqueSelect } from '../../../../components/parametros';
import { useEmpaques } from '../../../../hooks/useParametric';
import { MarcaSelect, ModeloSelect } from '../../marcas/components';
import { EspecieSelect, PresentacionSelect, CalibreSelect } from '../../especies/components';
import { type ArticuloEditDialogProps, type ArticuloResponse, type ArticuloRequest } from '../types/articulo';
import { ArticuloService } from '../services';

const ARTICULO_CONSTANTS = {
  DIALOG_CONFIG: {
    DEFAULT_TITLE: 'Artículo',
    MAX_WIDTH: 'md' as const,
  },
  FIELD_LABELS: {
    descripcion: 'Descripción',
    codigo: 'Código',
    marca: 'Marca',
    modelo: 'Modelo',
    ubicacion: 'Ubicación',
    precio: 'Precio',
    idCategoria: 'Categoría',
    idUnidadMedida: 'Unidad de Medida',
    idEstado: 'Estado',
    servicio: 'Servicio',
  },
};

/**
 * EditArticulo Component
 *
 * A comprehensive articulo editing dialog that combines form fields and dialog functionality.
 */

const EditArticulo: React.FC<ArticuloEditDialogProps> = ({
  open,
  articuloIdArticulo: articuloIdArticulo,
  onClose,
  onSave,
  title = ARTICULO_CONSTANTS.DIALOG_CONFIG.DEFAULT_TITLE,
}) => {
  // Local state for articulo data
  const [, setArticuloData] = useState<ArticuloResponse | null>(null);
  const [loadingArticulo, setLoadingArticulo] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const { data: empaques } = useEmpaques();

  // Note: Species-related state is now handled by individual select components


  // Additional form state for new fields
  const [formState, setFormState] = useState({
    // Datos Generales
    servicio: false,
    produccion: false,
    articulo: '',
    descripcion: '',
    codigoFabrica: '',
    ubicacion: '',
    codigoBarra: '',
    marca: '',
    modelo: '',
    categoria: '',

    // Parámetros de Stock
    habilitarControlStock: false,
    permiteStockNegativo: false,
    aplicaStockMinimo: false,
    stockMinimo: 0,
    puntoPedido: 0,
    puntoMaximo: 0,

    // Producción
    idEspecie: '',
    idCalibre: '',
    idEmpaqueExterno: '',
    idPresentacion: '',
    idEmpaqueInterno: '',
    idUnidadMedida: '',
    descripcionCompleta: ''
  });

  // Track selected labels for description generation
  const labelsRef = useRef<Record<string, string>>({});

  const updateLabel = useCallback((field: string, label: string) => {
    labelsRef.current[field] = label;
    // Auto-generate description from labels
    const parts = [
      labelsRef.current.especie,
      labelsRef.current.presentacion,
      labelsRef.current.calibre,
      labelsRef.current.empaqueInterno,
      labelsRef.current.empaqueExterno,
      labelsRef.current.unidadMedida,
    ].filter(Boolean);
    const desc = parts.join(' ');
    setFormState(prev => ({ ...prev, descripcionCompleta: desc }));
  }, []);

  // Compute isValid from formState - require either descripcion or descripcionCompleta
  const isValid = formState.descripcion.trim() !== '' || formState.descripcionCompleta.trim() !== '';

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleFormStateChange = (field: string, value: string | number | boolean) => {
    setFormState(prev => ({
      ...prev,
      [field]: value
    }));
    setIsDirty(true);
  };

  const handleFormSubmit = useCallback(async () => {
    if (isSubmitting || !isValid) return;

    setIsSubmitting(true);
    try {
      const request: ArticuloRequest = {
        ...(articuloIdArticulo ? { idArticulo: articuloIdArticulo } : {}),
        descripcion: formState.descripcion.trim() || formState.descripcionCompleta.trim(),
        descripcionVariable: 'N',
        codigoFabrica: formState.codigoFabrica || undefined,
        codigoBarra: formState.codigoBarra || undefined,
        ubica: formState.ubicacion || undefined,
        idMarca: formState.marca ? Number(formState.marca) : undefined,
        idModelo: formState.modelo ? Number(formState.modelo) : undefined,
        idEstado: 1,
        idEspecie: formState.idEspecie ? Number(formState.idEspecie) : undefined,
        idPresentacion: formState.idPresentacion ? Number(formState.idPresentacion) : undefined,
        idCalibre: formState.idCalibre ? Number(formState.idCalibre) : undefined,
        idEmpaqueInterior: formState.idEmpaqueInterno ? Number(formState.idEmpaqueInterno) : undefined,
        idEmpaqueExterior: formState.idEmpaqueExterno ? Number(formState.idEmpaqueExterno) : undefined,
        pesoEmpaqueInterior: formState.idEmpaqueInterno ? empaques.find(e => e.idEmpaque === Number(formState.idEmpaqueInterno))?.peso ?? undefined : undefined,
        pesoEmpaqueExterior: formState.idEmpaqueExterno ? empaques.find(e => e.idEmpaque === Number(formState.idEmpaqueExterno))?.peso ?? undefined : undefined,
        idUnidadMedida: formState.idUnidadMedida ? Number(formState.idUnidadMedida) : undefined,
        isServicio: formState.servicio,
        isProduccion: formState.produccion,
        manejaStock: formState.habilitarControlStock ? 'S' : 'N',
        stockNegativo: formState.permiteStockNegativo ? 'S' : 'N',
        aplicaStockMinimo: formState.aplicaStockMinimo,
        stockMinimo: formState.stockMinimo || undefined,
        puntoPedido: formState.puntoPedido || undefined,
        puntoMaximo: formState.puntoMaximo || undefined,
        manoDeObra: 'N',
        stockNumeroDeSerie: 'N',
        stockLote: 'N',
        imprimeComposicion: 'N',
      };

      await onSave(request);
      setIsDirty(false);
    } catch (error) {
      console.error('Error saving articulo:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [formState, articuloIdArticulo, isSubmitting, isValid, onSave]);


  // Fetch articulo data when dialog opens
  useEffect(() => {
    const fetchArticulo = async () => {
      if (!open || !articuloIdArticulo) {
        setArticuloData(null);
        return;
      }

      try {
        setLoadingArticulo(true);
        const data = await ArticuloService.getArticuloById(articuloIdArticulo);
        setArticuloData(data);

        // Update formState with loaded article data
        setFormState(prev => ({
          ...prev,
          // Datos Generales
          servicio: data.isServicio || false,
          produccion: data.isProduccion || false,
          articulo: data.descripcion || '',
          descripcion: data.descripcion || '',
          codigoFabrica: data.codigoFabrica || '',
          ubicacion: data.ubicacion || '',
          codigoBarra: data.codigoBarra || '',
          marca: data.idMarca ? String(data.idMarca) : '',
          modelo: data.idModelo ? String(data.idModelo) : '',
          // Parámetros de Stock
          habilitarControlStock: data.manejaStock === 'S',
          permiteStockNegativo: data.stockNegativo === 'S',
          aplicaStockMinimo: data.aplicaStockMinimo || false,
          stockMinimo: data.stockMinimo || 0,
          puntoPedido: data.puntoPedido || 0,
          puntoMaximo: data.puntoMaximo || 0,
          // Producción
          idEspecie: data.idEspecie ? String(data.idEspecie) : '',
          idPresentacion: data.idPresentacion ? String(data.idPresentacion) : '',
          idCalibre: data.idCalibre ? String(data.idCalibre) : '',
          idEmpaqueInterno: data.idEmpaqueInterior ? String(data.idEmpaqueInterior) : '',
          idEmpaqueExterno: data.idEmpaqueExterior ? String(data.idEmpaqueExterior) : '',
          idUnidadMedida: data.idUnidadMedida ? String(data.idUnidadMedida) : '',
          descripcionCompleta: data.descripcionVariable || '',
        }));
        // Initialize labels from backend navigation properties
        labelsRef.current = {
          especie: data.especieNombre || '',
          presentacion: data.presentacionNombre || '',
          calibre: data.calibreNombre || '',
          empaqueInterno: data.empaqueInteriorNombre || '',
          empaqueExterno: data.empaqueExteriorNombre || '',
          unidadMedida: data.unidadMedidaNombre || '',
        };
      } catch (error) {
        console.error('Error fetching articulo:', error);
        setArticuloData(null);
      } finally {
        setLoadingArticulo(false);
      }
    };

    fetchArticulo();
  }, [open, articuloIdArticulo]);

  // Handle new articulo case (no articuloIdArticulo)
  useEffect(() => {
    if (open && !articuloIdArticulo) {
      setArticuloData(null);

      // Reset labels
      labelsRef.current = {};
      // Reset formState to defaults for new article
      setFormState({
        // Datos Generales
        servicio: false,
        produccion: false,
        articulo: '',
        descripcion: '',
        codigoFabrica: '',
        ubicacion: '',
        codigoBarra: '',
        marca: '',
        modelo: '',
        categoria: '',

        // Parámetros de Stock
        habilitarControlStock: false,
        permiteStockNegativo: false,
        aplicaStockMinimo: false,
        stockMinimo: 0,
        puntoPedido: 0,
        puntoMaximo: 0,

        // Producción
        idEspecie: '',
        idCalibre: '',
        idEmpaqueExterno: '',
        idPresentacion: '',
        idEmpaqueInterno: '',
        idUnidadMedida: '',
        descripcionCompleta: ''
      });
    }
  }, [open, articuloIdArticulo]);

  // Reset tab and form state when dialog opens
  useEffect(() => {
    if (open) {
      setTabValue(0);
      setIsDirty(false);
      setIsSubmitting(false);
    }
  }, [open]);




  // Note: Data fetching is now handled by individual select components
  // Handle clearing dependent fields when parent selections change
  const handleEspecieChange = (value: string | number) => {
    handleFormStateChange('idEspecie', value);
    // Clear dependent fields and labels
    handleFormStateChange('idPresentacion', '');
    handleFormStateChange('idCalibre', '');
    labelsRef.current.presentacion = '';
    labelsRef.current.calibre = '';
  };

  const handlePresentacionChange = (value: string | number) => {
    handleFormStateChange('idPresentacion', value);
    // Clear dependent field and label
    handleFormStateChange('idCalibre', '');
    labelsRef.current.calibre = '';
  };






  // Note: Options are now handled by individual select components


  const handleCancelClick = (): void => {
    if (isDirty) {
      // In a real application, you might want to show a confirmation dialog
      // before discarding changes
    }
    onClose();
  };

  // Early return if still loading
  if (articuloIdArticulo && loadingArticulo) {
    return null;
  }

  const dialogTitle = `${title}${isDirty ? ' *' : ''}`;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <PopupDialog
        open={open}
        onClose={handleCancelClick}
        title={dialogTitle}
        maxWidth={ARTICULO_CONSTANTS.DIALOG_CONFIG.MAX_WIDTH}
        disableEscapeKeyDown={isDirty}
        primaryAction={{
          label: 'Guardar',
          onClick: handleFormSubmit,
          disabled: !isValid,
          loading: isSubmitting,
        }}
        secondaryAction={{
          label: 'Cancelar',
          onClick: handleCancelClick,
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="Datos Generales" />
              <Tab label="Parámetros de Stock" />
              <Tab label="Producción" />
            </Tabs>
          </Box>

          <TabPanelComponent value={tabValue} index={0}>
            {/* Datos Generales */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <CheckboxComponent
                label="Servicio"
                checked={formState.servicio}
                onChange={(checked) => handleFormStateChange('servicio', checked)}
              />
              <CheckboxComponent
                label="Producción"
                checked={formState.produccion}
                onChange={(checked) => handleFormStateChange('produccion', checked)}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <TextFieldComponent
                label="Descripción"
                value={formState.descripcion}
                onChange={(value) => handleFormStateChange('descripcion', value)}
                fullWidth
                required
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, mb: 2 }}>
              <TextFieldComponent
                label="Código Fábrica"
                value={formState.codigoFabrica}
                onChange={(value) => handleFormStateChange('codigoFabrica', value)}
                fullWidth
              />
              <TextFieldComponent
                label="Ubicación"
                value={formState.ubicacion}
                onChange={(value) => handleFormStateChange('ubicacion', value)}
                fullWidth
              />
              <TextFieldComponent
                label="Código Barra"
                value={formState.codigoBarra}
                onChange={(value) => handleFormStateChange('codigoBarra', value)}
                fullWidth
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
              <MarcaSelect
                value={formState.marca}
                onChange={(value: string | number) => handleFormStateChange('marca', value)}
                label="Marca"
              />
              <ModeloSelect
                value={formState.modelo}
                onChange={(value: string | number) => handleFormStateChange('modelo', value)}
                idMarca={formState.marca}
                label="Modelo"
              />
            </Box>

          </TabPanelComponent>

          <TabPanelComponent value={tabValue} index={1}>
            {/* Parámetros de Stock */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              <CheckboxComponent
                label="Habilitar Control de Stock"
                checked={formState.habilitarControlStock}
                onChange={(checked) => handleFormStateChange('habilitarControlStock', checked)}
              />
              <CheckboxComponent
                label="Permite Stock Negativo"
                checked={formState.permiteStockNegativo}
                onChange={(checked) => handleFormStateChange('permiteStockNegativo', checked)}
              />
              <CheckboxComponent
                label="Aplica Stock Mínimo"
                checked={formState.aplicaStockMinimo}
                onChange={(checked) => handleFormStateChange('aplicaStockMinimo', checked)}
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
              <TextFieldComponent
                label="Stock Mínimo"
                type="number"
                value={formState.stockMinimo}
                onChange={(value) => handleFormStateChange('stockMinimo', Number(value))}
                fullWidth
              />
              <TextFieldComponent
                label="Punto de Pedido"
                type="number"
                value={formState.puntoPedido}
                onChange={(value) => handleFormStateChange('puntoPedido', Number(value))}
                fullWidth
              />
              <TextFieldComponent
                label="Punto Máximo"
                type="number"
                value={formState.puntoMaximo}
                onChange={(value) => handleFormStateChange('puntoMaximo', Number(value))}
                fullWidth
              />
            </Box>
          </TabPanelComponent>

          <TabPanelComponent value={tabValue} index={2}>
            {/* Producción */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
              <EspecieSelect
                value={formState.idEspecie}
                onChange={handleEspecieChange}
                onSelect={(_v, label) => updateLabel('especie', label)}
                label="Especie"
              />
              <PresentacionSelect
                value={formState.idPresentacion}
                onChange={handlePresentacionChange}
                onSelect={(_v, label) => updateLabel('presentacion', label)}
                idEspecie={formState.idEspecie}
                label="Presentación"
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, mb: 2 }}>
              <CalibreSelect
                value={formState.idCalibre}
                onChange={(value: string | number) => handleFormStateChange('idCalibre', value)}
                onSelect={(_v, label) => updateLabel('calibre', label)}
                idPresentacion={formState.idPresentacion}
                label="Calibre"
              />
              <EmpaqueSelect
                label="Empaque Interno"
                value={formState.idEmpaqueInterno}
                onChange={(value: string | number) => handleFormStateChange('idEmpaqueInterno', value)}
                onSelect={(_v, label) => updateLabel('empaqueInterno', label)}
                isExterno={false}
              />
              <EmpaqueSelect
                label="Empaque Externo"
                value={formState.idEmpaqueExterno}
                onChange={(value: string | number) => handleFormStateChange('idEmpaqueExterno', value)}
                onSelect={(_v, label) => updateLabel('empaqueExterno', label)}
                isExterno={true}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <UnidadMedidaSelect
                value={formState.idUnidadMedida}
                onChange={(value: string | number) => handleFormStateChange('idUnidadMedida', value)}
                onSelect={(_v, label) => updateLabel('unidadMedida', label)}
                label="Unidad de Medida"
              />
            </Box>

            <TextFieldComponent
              label="Descripción Completa"
              value={formState.descripcionCompleta}
              onChange={(value) => handleFormStateChange('descripcionCompleta', value)}
              multiline
              rows={4}
              fullWidth
            />
          </TabPanelComponent>
        </Box>
      </PopupDialog>
    </LocalizationProvider>
  );
};

export default EditArticulo;